import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  Button,
  ConsentOrdersPathName,
  FormError,
  FormNavigation,
  RadioSelect,
  RealEstateBaseProps,
  RealEstateBaseSchema,
  RealEstate_InitialState,
  useNames,
  useRealEstate
} from "@fla/components";
import HouseIcon from "@fla/components/src/assets/svgs/house.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectAsset, selectConsentOrdersBase } from "pages/ConsentOrders";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_own_real_estate: ""
};
function RealEstate() {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const _useNames = useNames({ initialDetails });
  const { spouseFirstName } = _useNames;
  const realEstateData = useSelector(selectAsset(AssetType.REAL_ESTATE)) as RealEstate_InitialState[];

  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>("");

  const location = useLocation();
  const navigate = useNavigate();
  const { handleSubmit, watch, control, reset, getValues } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(RealEstateBaseSchema)
  });

  const editData = (values?: RealEstateBaseProps) => {
    navigate(`${ConsentOrdersPathName.RealEstate}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const goBack = () => navigate(ConsentOrdersPathName.InitialDetails);

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();
  const onSubmit = async (values: RealEstateBaseProps) => {
    try {
      if (values?.if_own_real_estate === "no" && realEstateData.length) {
        setError("If neither of you have any property in Australia, then please remove the added property details.");
        return;
      } else if (values?.if_own_real_estate === "yes" && realEstateData.length === 0) {
        setError("Please add at least one property.");

        return;
      } else {
        setError("");
        setStatus(SliceStatus.Pending);
        const payload = {
          real_estate: { data: values, isCompleted: true },
          currentStep: 2
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        navigate(ConsentOrdersPathName.Superannuation);
      }
      setStatus(SliceStatus.Resolved);
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(defaultValues);
    }
    // return () => {
    //   reset(defaultValues);
    // };
  }, [location]);

  const { sellCard, keepCard, paymentReceivingFromCard, userTotalAmount, spouseTotalAmount } = useRealEstate({
    realEstateData,
    ..._useNames
  });

  return (
    <section className="w-full py-10">
      <form
        id="real-estate-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5 space-y-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={HouseIcon} className="w-10 h-auto mx-auto my-3" alt="real estate" />

        <h3 className="text-center my-5">REAL ESTATE</h3>
        <RadioSelect
          title={`Do you or ${spouseFirstName} own any real estate in Australia?`}
          subTitle="Even if held with third parties, or through another entity like at trust."
          name="if_own_real_estate"
          id="if_own_real_estate"
          control={control}
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" }
          ]}
          type="card"
          classes="mt-12"
          onChange={() => setError("")}
          theme="dark"
        />

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="text-base font-bold">Please add the details of property</p>

          <Button
            className="px-5 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_own_real_estate") !== "yes"}
          >
            + Add Real Estate
          </Button>
        </div>

        {error ? (
          <div className="max-w-sm mx-auto text-center">
            <FormError error={error} />
          </div>
        ) : null}

        {Array.isArray(realEstateData) && realEstateData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

            {sellCard("user", editData)}
            {keepCard("user", editData)}
            {/* receiving payment from spouse */}
            {paymentReceivingFromCard("spouse")}

            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>
            {/* -------------------------------SPOUSE-------------------------- */}
            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

            {sellCard("spouse", editData)}
            {keepCard("spouse", editData)}
            {/* receiving payment from user */}
            {paymentReceivingFromCard("user")}

            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          form="real-estate-form"
          isSubmitting={status === SliceStatus.Pending}
          handleBackClick={goBack}
          isLast={false}
          classes="mt-20 mb-8"
        />
      </form>
    </section>
  );
}
export default withConsentOrder(RealEstate);
